import { useMemo, useState } from "react";
import {
  useDataProvider,
  useGetList,
  useRedirect,
  useTranslate,
} from "ra-core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  ExternalLink,
  History,
  Loader2,
  MessageCircle,
  RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";

import type { CrmDataProvider } from "../providers/types";
import type {
  Contact,
  ContactConversationMessage,
  EchoflicksSessionMedia,
  EchoflicksVideoSession,
} from "../types";
import {
  WhatsAppChat,
  WhatsAppChatHeader,
  buildConversationTimeline,
  buildEchoIndex,
  normalizeConversationMessage,
  whatsAppHistoryRows,
} from "../whatsapp/chat";
import {
  mergeConversationPages,
  reconcileOptimisticMessages,
} from "../whatsapp/inbox/conversationPages";
import { conversationQueryKey } from "../whatsapp/inbox/useInbox";
import { ContactAttentionBanner } from "./ContactAttentionBanner";
import { WhatsAppComposer } from "./WhatsAppComposer";

const PAGE_SIZE = 100;

type PendingSends = {
  contactId: Contact["id"];
  rows: ContactConversationMessage[];
};

export const ContactConversationTimeline = ({
  contact,
  heightClassName = "h-[80vh] min-h-[840px] sm:h-[1200px]",
}: {
  contact: Contact;
  /** Override the chat height (e.g. when embedded in a modal). */
  heightClassName?: string;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const redirect = useRedirect();
  // Optimistic operator bubbles, keyed by contact so switching records never
  // leaks a pending send into another thread.
  const [pendingSends, setPendingSends] = useState<PendingSends | null>(null);
  const pending = useMemo(
    () =>
      pendingSends && pendingSends.contactId === contact.id
        ? pendingSends.rows
        : [],
    [pendingSends, contact.id],
  );

  // Newest page first; "Load older" walks the cursor backwards. Polling
  // refetches the loaded pages while the tab is visible.
  const conversation = useInfiniteQuery({
    queryKey: conversationQueryKey(contact.id),
    queryFn: ({ pageParam }) =>
      dataProvider.getContactConversationPage(contact.id, {
        cursor: pageParam,
        limit: PAGE_SIZE,
        order: "DESC",
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.has_more && last.next_cursor ? last.next_cursor : undefined,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const serverRows = useMemo(
    () => mergeConversationPages(conversation.data?.pages ?? []),
    [conversation.data],
  );
  const optimisticRows = useMemo(
    () => reconcileOptimisticMessages(pending, serverRows),
    [pending, serverRows],
  );
  const messages = useMemo(
    () => [...serverRows, ...optimisticRows],
    [serverRows, optimisticRows],
  );

  // Echoes for this contact let us label/link each echo window in the thread.
  const { data: sessions = [] } = useGetList<EchoflicksVideoSession>(
    "echoflicks_video_sessions",
    {
      filter: { contact_id: contact.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "display_date", order: "DESC" },
    },
  );

  // User-submitted photos live in per-session `uploaded_media` (no contact-wide
  // endpoint), so fan out across the contact's recent echoes and collect them.
  // Cap the fan-out to keep the request count bounded; older echoes' images
  // simply fall back to the placeholder.
  const sessionIds = useMemo(
    () => sessions.map((session) => String(session.id)).slice(0, 30),
    [sessions],
  );

  const { data: mediaItems = [] } = useQuery({
    queryKey: ["contact-conversation-media", String(contact.id), sessionIds],
    enabled: sessionIds.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const lists = await Promise.all(
        sessionIds.map((sessionId) =>
          dataProvider
            .getList<EchoflicksSessionMedia>("echoflicks_session_media", {
              filter: { session_id: sessionId },
              pagination: { page: 1, perPage: 100 },
              sort: { field: "created_at_source", order: "ASC" },
            })
            .then((result) => result.data)
            .catch(() => [] as EchoflicksSessionMedia[]),
        ),
      );
      return lists.flat().filter((item) => item.kind === "uploaded_media");
    },
  });

  const contactName =
    [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() ||
    "Contact";

  const whatsappId =
    contact.whatsapp_user_id ??
    messages.find((message) => message.whatsapp_user_id)?.whatsapp_user_id ??
    null;

  const items = useMemo(() => {
    const echoIndex = buildEchoIndex(sessions);
    // MYC-090: a my.echoflicks.com turn is not a WhatsApp message. The indexer
    // keeps web turns out of this ledger (MYC-015); this is the belt to that.
    const normalized = whatsAppHistoryRows(messages).map((message) =>
      normalizeConversationMessage(message, contactName),
    );
    return buildConversationTimeline(normalized, { echoIndex, mediaItems });
  }, [messages, sessions, contactName, mediaItems]);

  const loading = conversation.isPending;
  const errorText = conversation.isError
    ? getErrorMessage(conversation.error)
    : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageCircle className="size-4 text-muted-foreground" />
        <h3 className="truncate text-lg font-semibold">
          {translate("crm.whatsapp.timeline_title")}
        </h3>
        {whatsappId ? (
          <a
            href={`/chat/c/${String(whatsappId).replace(/\D/g, "")}`}
            className="ml-auto inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
          >
            {translate("crm.whatsapp.open_in_inbox", {
              _: "Open in Felix Inbox",
            })}
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </a>
        ) : null}
      </div>

      <ContactAttentionBanner contact={contact} />

      {errorText && messages.length > 0 ? (
        <p className="text-xs text-destructive">{errorText}</p>
      ) : null}

      <WhatsAppChat
        items={items}
        heightClassName={heightClassName}
        onEchoOpen={(echoId) =>
          redirect(`/echoflicks_video_sessions/${echoId}/show`)
        }
        header={
          <WhatsAppChatHeader
            name={contactName}
            subtitle={whatsappId ?? "WhatsApp"}
            fallback={initialsOf(contactName)}
            actions={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 rounded-full text-[#54656f] hover:bg-black/5 dark:text-[#aebac1] dark:hover:bg-white/10"
                aria-label={translate("ra.action.refresh")}
                onClick={() => void conversation.refetch()}
                disabled={conversation.isFetching}
              >
                <RefreshCw
                  className={
                    conversation.isFetching && !loading
                      ? "size-4 animate-spin"
                      : "size-4"
                  }
                />
              </Button>
            }
          />
        }
        topSlot={
          conversation.hasNextPage ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-full bg-white/90 text-xs dark:bg-[#182229]"
              onClick={() => void conversation.fetchNextPage()}
              disabled={conversation.isFetchingNextPage}
            >
              {conversation.isFetchingNextPage ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <History className="size-3.5" />
              )}
              {conversation.isFetchingNextPage
                ? translate("crm.whatsapp.inbox.loading_older")
                : translate("crm.whatsapp.inbox.load_older")}
            </Button>
          ) : null
        }
        footer={
          <WhatsAppComposer
            contact={contact}
            onSent={(message) =>
              setPendingSends((current) => ({
                contactId: contact.id,
                rows: [
                  ...(current && current.contactId === contact.id
                    ? current.rows
                    : []),
                  message,
                ],
              }))
            }
          />
        }
        emptyState={
          <p className="text-sm text-[#54656f] dark:text-[#8696a0]">
            {loading
              ? translate("crm.whatsapp.timeline_loading")
              : errorText
                ? errorText
                : translate("crm.whatsapp.timeline_empty")}
          </p>
        }
      />
    </div>
  );
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "crm.whatsapp.timeline_error";
}
